// Auth Service - Handles login, signup, token management, and tenant-aware auth

import { UserProfile } from '../types';
import api, {
  getApiBase,
  getRefreshApiBase,
  TENANT_DOMAIN_KEY,
  setAccessToken,
  getAccessToken,
  getRefreshedAccessToken,
  clearAuthStorage,
} from './api';
import {
  setUserPermissions,
  setUserHierarchyLevel,
  setUserAssignedRole,
  clearUserPermissions,
} from '../utils/featureFlagsStore';

export interface LoginPayload {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: string; // email
  id: string;
  first_name: string;
  last_name: string;
  role: string; // 'OWNER', 'MANAGER', 'OPERATOR' (uppercase) — normalised to title-case by normalizeRole
  tenant: number | Record<string, any>;
  domain?: string;
  // RBAC fields
  assigned_role?: string | null;
  hierarchy_level?: number | null;
  permissions?: string[];
  // access token only — refresh token is delivered via httpOnly cookie, never in the body
  access: string;
}

export interface SignupPayload {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone_code?: string;
  phone_number?: string;
  role?: string;
}

export interface SignupResponse {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  message: string;
}

export interface RefreshTokenPayload {
  refresh: string;
}

export interface RefreshTokenResponse {
  access: string;
}

export interface AuthUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'Owner' | 'Manager' | 'Staff';
  assigned_role?: string | null;
  tenant_name?: string;
}

// Non-sensitive session keys stored in localStorage.
// The access token lives in JS memory only (api.ts:_accessToken).
// The refresh token lives in an httpOnly cookie managed by the backend.
const USER_KEY = 'auth_user';
const TENANT_ID_KEY = 'tenant_id';
const LOGGED_IN_KEY = 'logged_in';

/** Extract numeric tenant_id from login/signup response tenant field */
const extractTenantId = (tenant: number | Record<string, any>): number | null => {
  if (typeof tenant === 'number') return tenant;
  if (typeof tenant === 'object' && tenant !== null && typeof tenant.id === 'number') return tenant.id;
  return null;
};

// Helper function to convert uppercase role to title case.
// Backend role choices: OWNER, MANAGER, OPERATOR (admin_users/models.py).
// OPERATOR is the field-level role for floor/production staff and maps to 'Staff' here.
const normalizeRole = (role: string): 'Owner' | 'Manager' | 'Staff' => {
  const roleMap: Record<string, 'Owner' | 'Manager' | 'Staff'> = {
    'OWNER': 'Owner',
    'MANAGER': 'Manager',
    'OPERATOR': 'Staff',
    'STAFF': 'Staff',
  };
  return roleMap[role?.toUpperCase()] || 'Staff';
};



export const authService = {
  // ============================================================================
  // LOGIN
  // ============================================================================
  login: async (payload: LoginPayload): Promise<LoginResponse> => {
    // Tenant is unknown at login time — use getApiBase() which resolves to origin in production
    const url = `${getApiBase()}/auth/login/`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    });

    if (!res.ok) {
      if (res.status >= 500) {
        // Try to parse a structured message; fall back to a friendly generic one.
        let serverMsg = 'Unable to sign in right now. Please try again in a moment.';
        try {
          const body = await res.json();
          if (body?.detail && !body.detail.toLowerCase().includes('internal server error')) {
            serverMsg = body.detail;
          }
        } catch { /* ignore parse errors */ }
        throw new Error(serverMsg);
      }
      const error = await res.json();
      throw new Error(error.detail || error.message || 'Login failed');
    }

    const data = await res.json() as LoginResponse;

    // Store the short-lived access token in JS memory only (never localStorage).
    // The refresh token arrived as an httpOnly cookie and is not visible here.
    setAccessToken(data.access);
    localStorage.setItem(LOGGED_IN_KEY, 'true');

    const userToStore: AuthUser = {
      id: data.id,
      email: data.user,
      first_name: data.first_name,
      last_name: data.last_name,
      role: normalizeRole(data.role),
      assigned_role: data.assigned_role ?? null,
    };

    localStorage.setItem(USER_KEY, JSON.stringify(userToStore));

    // Hydrate the RBAC permission store from the login response
    setUserPermissions(data.permissions ?? []);
    setUserHierarchyLevel(data.hierarchy_level ?? null);
    setUserAssignedRole(data.assigned_role ?? null);
    // Always overwrite (or clear) the tenant domain so a null-tenant user never
    // inherits a stale domain left in localStorage from a previous session.
    const loginDomain = data.domain || (typeof data.tenant === 'object' && data.tenant?.domain);
    if (loginDomain) {
      localStorage.setItem(TENANT_DOMAIN_KEY, loginDomain);
    } else {
      localStorage.removeItem(TENANT_DOMAIN_KEY);
    }
    const tenantId = extractTenantId(data.tenant);
    if (tenantId !== null) {
      localStorage.setItem(TENANT_ID_KEY, String(tenantId));
    } else {
      localStorage.removeItem(TENANT_ID_KEY);
    }
    return data;
  },

  // ============================================================================
  // SIGNUP
  // ============================================================================
  signup: async (payload: SignupPayload): Promise<SignupResponse> => {
    // Set default phone code
    const signupPayload = {
      ...payload,
      phone_code: payload.phone_code || '+91',
    };

    // Tenant is unknown at signup time — use getApiBase() which resolves to origin in production
    const url = `${getApiBase()}/auth/signup/`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(signupPayload),
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || error.message || 'Signup failed');
    }

    const data = await res.json() as SignupResponse;

    // Signup creates an inactive account — no tokens are issued until admin approval.
    // Simply return the confirmation payload; the caller should show the approval message.
    return data;
  },

  // ============================================================================
  // REFRESH TOKEN
  // ============================================================================
  refreshToken: async (): Promise<string> => {
    try {
      return await getRefreshedAccessToken();
    } catch {
      await authService.logout();
      throw new Error('Token refresh failed');
    }
  },

  // ============================================================================
  // LOGOUT
  // ============================================================================
  logout: async () => {
    const token = getAccessToken();
    try {
      await fetch(`${getRefreshApiBase()}/auth/logout/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    } catch {
      // Local session is cleared even if the server call fails.
    }

    setAccessToken(null);
    clearAuthStorage();
    clearUserPermissions();
  },

  // ============================================================================
  // GET STORED TOKEN
  // ============================================================================
  getAccessToken: (): string | null => {
    return getAccessToken();
  },

  getTenantId: (): number | null => {
    const val = localStorage.getItem(TENANT_ID_KEY);
    if (!val) return null;
    const num = Number(val);
    return Number.isFinite(num) ? num : null;
  },

  // ============================================================================
  // GET CURRENT USER
  // ============================================================================
  getCurrentUser: (): AuthUser | null => {
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) return null;
    try {
      const user = JSON.parse(userStr) as AuthUser;
      // Ensure role has a value and normalize it to title case so RBAC comparisons work
      if (!user.role) {
        user.role = 'Staff'; // Default role if missing
      } else {
        user.role = normalizeRole(user.role as string);
      }
      return user;
    } catch {
      console.error('Error parsing user data');
      return null;
    }
  },

  // ============================================================================
  // CHECK IF AUTHENTICATED
  // ============================================================================
  isAuthenticated: (): boolean => {
    // Primary check: access token in memory (present after login or silent refresh).
    // Secondary: logged_in flag tells us a valid session *may* exist via the httpOnly
    // cookie even after a page reload (the silent refresh in initAccessToken() will
    // confirm or reject it).
    return !!getAccessToken() || localStorage.getItem(LOGGED_IN_KEY) === 'true';
  },

  // ============================================================================
  // GET USER ROLE
  // ============================================================================
  getUserRole: (): string | null => {
    const user = authService.getCurrentUser();
    return user?.role || null;
  },

  /**
   * Human-readable role label for UI display.
   * Prefers RBAC assigned_role (e.g. "Finance Manager") over coarse role (e.g. "Staff").
   */
  getDisplayRole: (): string => {
    const user = authService.getCurrentUser();
    if (!user) return 'User';
    return user.assigned_role || user.role || 'User';
  },

  _persistUserFields: (fields: Partial<Pick<AuthUser, 'assigned_role' | 'tenant_name'>>): void => {
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) return;
    try {
      const user = JSON.parse(userStr) as AuthUser;
      if (fields.assigned_role !== undefined) {
        user.assigned_role = fields.assigned_role ?? null;
      }
      if (fields.tenant_name !== undefined) {
        user.tenant_name = fields.tenant_name || undefined;
      }
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch {
      // ignore malformed localStorage
    }
  },

  _persistAssignedRole: (assignedRole: string | null | undefined): void => {
    authService._persistUserFields({ assigned_role: assignedRole });
  },

  _hydrateProfileSession: (profile: UserProfile): UserProfile => {
    if (profile.assigned_role !== undefined) {
      setUserAssignedRole(profile.assigned_role ?? null);
      authService._persistAssignedRole(profile.assigned_role);
    }
    if (profile.tenant_name !== undefined) {
      authService._persistUserFields({ tenant_name: profile.tenant_name });
    }
    if (profile.permissions !== undefined) {
      setUserPermissions(profile.permissions ?? []);
    }
    if (profile.hierarchy_level !== undefined) {
      setUserHierarchyLevel(profile.hierarchy_level ?? null);
    }
    return profile;
  },

  // ============================================================================
  // GET USER DETAILS FROM API
  // ============================================================================
  getUserDetails: async (): Promise<UserProfile> => {
    const data = await api.get<UserProfile>('/auth/me/');
    return authService._hydrateProfileSession(data);
  },

  updateProfile: async (payload: { first_name?: string; last_name?: string }): Promise<UserProfile> => {
    const data = await api.patch<UserProfile>('/auth/me/', payload);
    return authService._hydrateProfileSession(data);
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    try {
      await api.post('/auth/me/change-password/', {
        current_password: currentPassword,
        new_password: newPassword,
      });
    } catch (err: any) {
      const error = err?.responseData || {};
      const message =
        error.current_password?.[0] ||
        error.new_password?.[0] ||
        error.detail ||
        'Failed to change password';
      throw new Error(message);
    }
  },
};
